import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Navigation, Pagination, Autoplay } from 'swiper/modules';

import { useMedia } from 'util/event';
import { BannerResource } from 'util/api/swaggerApi/data-contracts';
import { isExist, getYouTubeVideoId } from 'util/helper';

import Link from 'components/atoms/Link';

import IconArrow from 'images/icon/arrow-right.inline.svg';

import YouTube from 'react-youtube';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import styles from './index.module.css';


interface CarouselProperty {
	data: BannerResource[];
}

const CarouselBanner: React.FC<CarouselProperty> = ({ data }) => {
	const swiperRef: any = useRef()
	const media = useMedia();
	const [activeBannerIndex, setActiveBannerIndex] = useState(0);
	const isTypeVideo = isExist(data[activeBannerIndex].youtubeLink);

	const videoAutoPlay: { [key: string]: string } = {
		DEFAULT: '0',
		AUTOPLAY_OFF: '0',
		AUTOPLAY_ON: '1',
	};

	const pagination = {
		el: `.${styles.carouselPagination}`,
		className: `.${styles.carouselItem}`,
		bulletActiveClass: `${styles.active}`,
		bulletClass: `${styles.paginationItem}`,
		clickable: true,
		renderBullet(index: number, className: string) {
			return `<div class="${className}">
								<div class="${styles.bulletWrapper}"></div>
								<span>${index + 1}</span>
							</div>`;
		},
	};

	return (
		<div className={styles.carouselBanner}>
			<Swiper
				className={styles.carousel}
				modules={[EffectFade, Navigation, Pagination, Autoplay]}
				spaceBetween={30}
				effect="fade"
				slidesPerView={1}
				navigation={{
					prevEl: '.button-prev',
					nextEl: '.button-next',
				}}
				pagination={pagination}
				onInit={swiper => {
					swiperRef.current = swiper;
				}}

				onRealIndexChange={swiper => {
					swiperRef.current = swiper;
					const currentBanner = data[swiper.realIndex];

					const currentBannerIsVideo = isExist(currentBanner.youtubeLink);
					const currentBannerIsAutoPlay =
						currentBannerIsVideo && currentBanner.autoPlay === 'AUTOPLAY_ON';
					swiper.navigation.update();
					setActiveBannerIndex(swiper.realIndex);

					// 手動換slider時檢查，恢復正常輪播
					if (!currentBannerIsAutoPlay) {
						swiper.autoplay.start();
					}
				}}
				autoplay={{ delay: 5000 }}
				loop
			>
				{data.slice(0, 10).map((item, i) => {
					const isAutoPlay =
						item.autoPlay && media === 'desktop'
							? videoAutoPlay[item.autoPlay]
							: videoAutoPlay.DEFAULT;

					const bannerImage = () => {
						if (media !== 'desktop' && isExist(item.imageMobile)) {
							return item.imageMobile;
						}
						return item.image;
					};

					// 影片結束或暫停，只要就繼續輪播
					const _onEnd = () => {
						swiperRef.current.autoplay.start()
					};

					// 不管是autoplay 或手動，只要一開始撥放就停止輪播
					const _onPlay = () => {
						swiperRef.current.autoplay.stop()
					};

					return (
						<SwiperSlide key={item.id} className={styles.carouselItem}>
							{ isTypeVideo && swiperRef.current?.realIndex === i ? (
								<YouTube
									videoId={getYouTubeVideoId(item.youtubeLink)}
									opts={{
										playerVars: {
										  autoplay: isAutoPlay,
										  mute: 1,
										  rel: 0
										}
									  }}
									onPlay={_onPlay}
									onEnd={_onEnd}
									onPause={_onEnd}
									className={styles.iframeContainer}
									title="YouTube video player" />
							) : (
								<Link
									className={classnames(styles.imageWrapper, {
										[styles.nonFullWidth]: media !== 'desktop' && !isExist(item.imageMobile),
									})}
									to={item.link}
								>
									<img className={styles.image} src={bannerImage()} alt="" />
								</Link>
							)}
						</SwiperSlide>
					);
				})}
			</Swiper>
			<button type="button" className={classnames('button-prev', styles.buttonPrev)} aria-label="prev">
				<IconArrow />
			</button>
			<button type="button" className={classnames('button-next', styles.buttonNext)} aria-label="next">
				<IconArrow />
			</button>
			<div className={styles.cardWrapper}>
				<Link to={data[activeBannerIndex].link}>
					<p className={styles.title}>{data[activeBannerIndex].name}</p>
					{data[activeBannerIndex].subtitle && (
						<p className={styles.subTitle}>{data[activeBannerIndex].subtitle}</p>
					)}
				</Link>
				<div
					className={classnames(styles.carouselPagination, {
						[styles.withoutGap]: data.length > 8,
					})}
				/>
			</div>
		</div>
	);
};

export default CarouselBanner;
